// REFERENCE: https://date-fns.org
import addMilliseconds from 'date-fns/add_milliseconds';
import differenceInMilliseconds from 'date-fns/difference_in_milliseconds';
import distanceInWords from 'date-fns/distance_in_words';
import distanceInWordsStrict from 'date-fns/distance_in_words_strict';
import distanceInWordsToNow from 'date-fns/distance_in_words_to_now';
import isBefore from 'date-fns/is_before';
import isAfter from 'date-fns/is_after';
import isEqual from 'date-fns/is_equal';
import isFuture from 'date-fns/is_future';
import isPast from 'date-fns/is_past';
import formatToString from 'date-fns/format';

/**
 * Accepts either a Date object or a date string in ISO format (eg '2018-12-30T16:45'),
 * the provided date is considered to be a UTC date/time representation (even though Date objects are in local timezone).
 * 
 * The provided date/time values are used to create and return a new Date object which represents the date/time
 * in your local timezone for the provided UTC date/time. JS Date objects are always in local timezone.
 * 
 * Eg: for "UTC" input of '2018-12-30T16:45' will become '2018-12-30T8:45' (assuming a local timezone of UTC-8).
 * 
 * Other functionality, including the built in date.toUTCString can be used to output in UTC if needed.
 * 
 * @param {string|Date} utcDate The date representation of your UTC date/time value.
 */
function fromUTC(utcDate) {
    if (typeof utcDate === 'string') {
        // Append 'Z' onto the ISO formatted datetime to turn it into UTC (unless Z already exists).
        if (!utcDate.endsWith('Z'))
            utcDate = utcDate + 'Z';

        return new Date(utcDate);
    }
    if (utcDate instanceof Date) {
        // Use the date/time values from the provided date object as if they are UTC (even though internally JS reports as locale timezone),
        // with those values we calculate the UTC timestamp and use that to create a new Date object with the UTC time accordingly.
        // Note we call getHours / etc rather than getUTCHours / etc since that would negate the conversion we're doing.
        return new Date(Date.UTC(
            utcDate.getFullYear(), utcDate.getMonth(), utcDate.getDate(),
            utcDate.getHours(), utcDate.getMinutes(), utcDate.getSeconds(), utcDate.getMilliseconds()));
    }

    throw new Error('Invalid Argument: tools.date.fromUTC(' + utcDate + ')');
}

/**
 * Uses the provided Date object (which, as always, is in local timezone) and outputs the equivalent UTC date/time
 * in ISO standard format. The local to UTC conversion is done by the built-in Date methods (eg getUTCMonth()).
 * 
 * Eg: input localDate of 2018-12-30 8:45 would output '2018-12-30T16:45:00Z'.
 * 
 * If you DO NOT want to convert to UTC but want to output in ISO standard format (what our APIs accept)
 * then you can use the built-in localDate.toISOString() instead.
 * 
 * @param {Date} localDate
 */
function toStringUTC(localDate) {
    if (!localDate) return null;

    const asTwoDigits = (n,p) => n < 10 ? '0' + n : n;

    const year = localDate.getUTCFullYear(),
        month = asTwoDigits(localDate.getUTCMonth()),
        date = asTwoDigits(localDate.getUTCDate()),
        hours = asTwoDigits(localDate.getUTCHours()),
        minutes = asTwoDigits(localDate.getUTCMinutes()),
        seconds = asTwoDigits(localDate.getUTCSeconds());

    return `${year}-${month}-${date}T${hours}:${minutes}:${seconds}Z`;
}

export const date = {
    fromUTC,
    toStringUTC,

    addMilliseconds,
    differenceInMilliseconds,

    isBefore,
    isAfter,
    isEqual,
    isFuture,
    isPast,

    distanceInWords,
    distanceInWordsStrict,
    distanceInWordsToNow,

    format: (value, dateFormat) => {
        if (!dateFormat) dateFormat = 'MMM Do YYYY, HH:mm';
        else if (dateFormat === 'date') dateFormat = 'MMM Do YYYY';
        else if (dateFormat === 'time') dateFormat = 'HH:mm';
        // else custom format

        if (!value) return null;
        else if (!(value instanceof Date)) value = fromUTC(value);

        return formatToString(value, dateFormat);
    }
};